import { ComparisonPoints } from "./comparison-points.js";
import { HoleComparisons } from "./comparison-course-holes.js";
import { ComparisonMetricTable } from "./comparison-metric-table.js";
import { ComparisonTestHistory } from "./comparison-test-history.js";
import { displayRecord } from "./comparison-presentation.js";
import { formatLabel } from "../../lib/format.js";
import {
  ComparisonNames,
  type AnalysisProps,
} from "./comparison-observation.js";
import { sourceDate, teePairLabel } from "./comparison-selection.js";
import { ComparisonPerformance } from "./comparison-performance.js";
import "../../styles/comparison-statistics.css";

export type ComparisonTopic =
  "overview" | "holes" | "courses" | "conditions" | "history" | "accuracy";
export function ComparisonSection(
  props: AnalysisProps & { topic: ComparisonTopic },
): React.JSX.Element {
  if (props.topic === "holes") return <HoleComparisons {...props} />;
  if (props.topic === "courses") return <CourseComparisons {...props} />;
  if (props.topic === "conditions") return <ConditionComparisons {...props} />;
  if (props.topic === "history") return <ComparisonHistory {...props} />;
  if (props.topic === "accuracy") return <AccuracyComparisons {...props} />;
  if (!Object.values(props.detail.phases).some((item) => item.n))
    return (
      <p className="rc-empty">
        {props.detail.sources.length
          ? "No shared hole scores for these filters."
          : "Play your first round together to build your stats."}
      </p>
    );
  return (
    <section className="rc-analysis rc-statistics">
      <ComparisonPerformance {...props} />
      <h2>The round, compared</h2>
      <ComparisonMetricTable
        {...props}
        rows={(
          [
            ["opening", "Opening hole"],
            ["front9", "First 9"],
            ["back9", "Last 9"],
            ["closing3", "Closing 3"],
          ] as const
        ).map(([key, label]) => ({
          label,
          observation: props.detail.phases[key],
        }))}
      />
      <AccuracyComparisons {...props} />
    </section>
  );
}
function CompactRecord({
  detail,
  record,
  label,
  caption,
}: AnalysisProps & {
  record: AnalysisProps["detail"]["record"];
  label: string;
  caption?: string;
}): React.JSX.Element {
  const [first, second] = displayRecord(detail.relation, record);
  return (
    <ComparisonPoints
      label={label}
      names={[first!.name, second!.name]}
      values={[first!.wins, second!.wins]}
      description="Points won · Higher is better"
      higherWins
      decimals={0}
      sample={`${caption ? `${caption} � ` : ""}${record.roundCount} rounds${record.ties ? ` � ${record.ties} tied` : ""}`}
    />
  );
}
function CourseComparisons(props: AnalysisProps): React.JSX.Element {
  return (
    <section className="rc-analysis rc-statistics">
      <h2>Course record</h2>
      <ComparisonNames {...props} />
      {props.detail.courses.length ? (
        props.detail.courses.map((row) => (
          <CompactRecord
            key={`${row.courseId}:${row.teePair}`}
            {...props}
            record={row.record}
            label={row.courseName}
            caption={teePairLabel(row.teePair)}
          />
        ))
      ) : (
        <p className="rc-empty">No shared courses yet.</p>
      )}
    </section>
  );
}
function ConditionComparisons(props: AnalysisProps): React.JSX.Element {
  const known = props.detail.conditions.filter(
    (row) =>
      !row.value.includes("unknown") &&
      row.record.roundCount > 0 &&
      !["left-cart", "right-cart"].includes(row.value),
  );
  const labels: Record<string, string> = {
    "both-cart": "Both driving",
    "both-walking": "Both walking",
    mixed: "Walk vs. drive",
    "weather-rain": "Rain",
  };
  return (
    <section className="rc-analysis rc-statistics">
      <h2>Conditions record</h2>
      <ComparisonNames {...props} />
      {known.length ? (
        known.map((row) => (
          <CompactRecord
            key={row.value}
            {...props}
            record={row.record}
            label={labels[row.value] ?? row.question}
          />
        ))
      ) : (
        <p className="rc-empty">No recorded conditions to compare yet.</p>
      )}
    </section>
  );
}
function AccuracyComparisons(props: AnalysisProps): React.JSX.Element | null {
  const metrics = (
    [
      ["putts", "Putts per hole"],
      ["gir", "Greens in regulation"],
      ["fairways", "Fairways hit"],
    ] as const
  ).filter(([key]) => props.detail.extra[key].n > 0);
  if (!metrics.length) return null;
  return (
    <section className="rc-analysis rc-statistics">
      <h2>Putting & accuracy</h2>
      <ComparisonMetricTable
        {...props}
        rows={metrics.map(([key, label]) => ({
          label,
          observation: props.detail.extra[key],
        }))}
      />
    </section>
  );
}
function ComparisonHistory({
  detail,
  onOpenRoundId,
}: AnalysisProps): React.JSX.Element {
  return (
    <section className="rc-analysis rc-statistics">
      <h2>Rounds together</h2>
      {!detail.sources.length && !detail.relation.testRounds?.length ? (
        <p className="rc-empty">Your first round belongs here.</p>
      ) : null}
      {[...detail.sources]
        .sort((a, b) => (b.playedAt ?? "").localeCompare(a.playedAt ?? ""))
        .map((source) => (
          <button
            key={source.roundId}
            className="rc-history-row"
            disabled={!onOpenRoundId}
            onClick={() => onOpenRoundId?.(source.roundId)}
          >
            <span className="rc-history-date">{sourceDate(source)}</span>
            <strong>{source.courseName}</strong>
            {source.contests.map((contest) => (
              <span className="rc-history-result" key={contest.id}>
                <b>
                  {contest.winner === "tie"
                    ? "Tied"
                    : `${detail.relation[contest.winner].name} won`}
                </b>
                <span>
                  {contest.label || formatLabel(contest.format)}
                  {contest.scope === "rivalry_only" ? " · Extra point" : ""}
                </span>
              </span>
            ))}
            {!source.contests.length ? (
              <span>
                Played together ·{" "}
                {formatLabel(source.sharedFormat ?? "stroke-play")}
              </span>
            ) : null}
            <span className="rc-history-open" aria-hidden="true">
              ↗
            </span>
          </button>
        ))}
      <ComparisonTestHistory relation={detail.relation} />
    </section>
  );
}
